import type { ComponentType, ElementType, ReactElement, Ref } from "react";

import { Tree } from "../treePrimitive";
import type {
  ArboristTreeApi,
  TreeNodeData,
  TreeNodeRenderProps,
} from "../treeTypes";

type DesignSystemTreeProps<T extends TreeNodeData = TreeNodeData> = {
  data: TreeNodeData[];
  openByDefault: boolean;
  initialOpenState?: Record<string, boolean> | undefined;
  selectionFollowsFocus?: boolean | undefined;
  disableMultiSelection?: boolean | undefined;
  rowHeight: number;
  indent: number;
  overscanCount: number;
  width: number;
  height: number;
  treeRef?: Ref<ArboristTreeApi<T> | null>;
  /** Omit to use arborist's built-in container (avoids duplicate TreeApi context). */
  renderContainer?: ElementType;
  renderRow?: ElementType;
  onToggle?: (id: string) => void;
  children: ComponentType<TreeNodeRenderProps<T>>;
};

type ArboristTreeComponent = <T extends TreeNodeData>(
  props: DesignSystemTreeProps<T> & {
    ref?: Ref<ArboristTreeApi<T> | null>;
  }
) => ReactElement;

const TypedTree = Tree as unknown as ArboristTreeComponent;

function DesignSystemTree<T extends TreeNodeData>({
  data,
  openByDefault,
  initialOpenState,
  selectionFollowsFocus,
  disableMultiSelection,
  rowHeight,
  indent,
  overscanCount,
  width,
  height,
  treeRef,
  renderContainer,
  renderRow,
  onToggle,
  children: Node,
}: DesignSystemTreeProps<T>): ReactElement {
  return (
    <TypedTree<T>
      {...(treeRef != null ? { ref: treeRef } : {})}
      data={data}
      openByDefault={openByDefault}
      {...(initialOpenState != null ? { initialOpenState } : {})}
      {...(selectionFollowsFocus != null ? { selectionFollowsFocus } : {})}
      {...(disableMultiSelection != null ? { disableMultiSelection } : {})}
      rowHeight={rowHeight}
      indent={indent}
      overscanCount={overscanCount}
      width={width}
      height={height}
      {...(renderContainer != null ? { renderContainer } : {})}
      {...(renderRow != null ? { renderRow } : {})}
      {...(onToggle != null ? { onToggle } : {})}
    >
      {Node}
    </TypedTree>
  );
}

export default DesignSystemTree;
export { Tree } from "../treePrimitive";
